import {ProductPriceData} from './product-price-data';
import {ProductSeason, seasonInForce} from './product-season';

export class ProductData {
  id = '';
  uid = '';
  name = '';
  description = '';
  createdOn = Date.now();
  enabled = true;
  // Area pricing is OPTIONAL — a product can be area-only, distance-only, or both.
  // New products default to area pricing; it's dropped if disabled in the editor.
  pricing?: ProductPriceData = new ProductPriceData();
  distancePricing?: ProductPriceData;
  /**
   * A price per marker (unit 'each', one fixed rate). Optional: without it a
   * marker is charged the area rate once, as markers always were, which made
   * a "per m²" product charge its square-metre price per tree, and a product
   * priced only by length nothing at all.
   */
  itemPricing?: ProductPriceData;
  icon = '';
  /** Swatch colour used to identify the product (legend, dashboard). */
  color = '#0ea5e9';
  /**
   * Product photo, shown in catalogue lists and pickers so a material can be
   * recognised by sight rather than read by name. Purely identification — it
   * is never drawn on the map.
   */
  thumbnailUrl?: string;
  /**
   * Seamless, tileable image of the material itself, drawn as the fill of any
   * polygon assigned this product. This is what lets someone see a roof in a
   * given shingle before they buy it.
   *
   * Distinct from `thumbnailUrl` on purpose: a catalogue photo of a bundle of
   * shingles makes a terrible tile, and a flat tileable swatch makes a dull
   * catalogue photo.
   */
  textureUrl?: string;
  /**
   * How much GROUND the texture image covers per tile, in metres.
   *
   * The texture is scaled to real-world size rather than to screen pixels, so
   * a shingle stays the same physical size as the map zooms and two roofs
   * surveyed at different zooms look consistent. Without this the fill reads
   * as wallpaper instead of as a material you could order.
   *
   * Undefined falls back to TEXTURE_DEFAULT_TILE_METERS.
   */
  textureTileMeters?: number;
  /** Optional grouping label (e.g. "Materials", "Labour"). */
  category = '';
  /** Soft-delete: archived products are hidden from active lists but not removed. */
  archived = false;
  /**
   * What the product costs YOU, per the same unit the sell price uses.
   *
   * Internal only: it never reaches an estimate, an export or a client-facing
   * document — it exists so the contractor can see the margin they are quoting
   * at while they set the price. 0 or undefined means "not tracked".
   */
  costPrice?: number;
  /**
   * Tax rate for this product, as a percentage.
   *
   * Overrides the project's rate for lines using this product, so a quote can
   * mix rates — materials at one rate, labour at another — which is the normal
   * case in most of Europe. Undefined means "use the project's rate"; 0 means
   * "explicitly untaxed", so the two are deliberately not the same.
   */
  taxRate?: number;
  /**
   * Prices that apply for part of the year, checked before the base price.
   *
   * Absent or empty means the product is priced the same all year, which is
   * every product that existed before this — nothing changes for them.
   */
  seasons?: ProductSeason[];
}

/**
 * The area pricing in force on `at`: the season's if one covers that date,
 * otherwise the product's own.
 *
 * Resolved at the point of use rather than written onto the product, so a
 * season starting tomorrow needs no job to apply it and no estimate is
 * silently re-priced by the calendar turning over.
 */
export function effectivePricing(product: ProductData, at: Date = new Date()): ProductPriceData | undefined {
  const season = seasonInForce(product?.seasons, at);
  return season?.pricing ?? product?.pricing;
}

/** The distance pricing in force on `at`. See effectivePricing. */
export function effectiveDistancePricing(product: ProductData, at: Date = new Date()): ProductPriceData | undefined {
  const season = seasonInForce(product?.seasons, at);
  return season?.distancePricing ?? product?.distancePricing;
}

/**
 * Ground covered by one texture tile when a product does not say.
 *
 * This is the whole IMAGE, not one shingle — a material photo normally shows
 * several courses, so a metre of roof per tile is the realistic reading.
 *
 * Chosen by rendering it: satellite imagery bottoms out around 6 cm per pixel,
 * so a 0.4 m tile lands on ~6 px and averages to a flat tone no matter how
 * good the photo is. At 1 m the courses survive, which is the entire point of
 * showing a customer the material rather than a colour.
 */
export const TEXTURE_DEFAULT_TILE_METERS = 1;

/** Ground per tile for this product, always a usable positive number. */
export function textureTileMeters(product: ProductData): number {
  const meters = product?.textureTileMeters;
  return meters && meters > 0 ? meters : TEXTURE_DEFAULT_TILE_METERS;
}

/**
 * How a product should be shown as a small square.
 *
 * The fallback order is deliberate — most recognisable first. A catalogue
 * photo identifies a material instantly; failing that the texture is the
 * material itself, just tiled rather than photographed; failing both, the
 * colour swatch is all we have. Colour last because it identifies the product
 * only to whoever chose it.
 *
 * One definition because four places draw this square — the three pickers and
 * the products table — and a product that looked different between them would
 * defeat the point of having a picture at all.
 */
export type ProductSwatchKind = 'photo' | 'texture' | 'color';

export interface ProductSwatch {
  kind: ProductSwatchKind;
  /** An image URL for photo/texture, a CSS colour for color. */
  value: string;
}

export function productSwatch(product: ProductData): ProductSwatch {
  if (product?.thumbnailUrl) {
    return {kind: 'photo', value: product.thumbnailUrl};
  }
  if (product?.textureUrl) {
    return {kind: 'texture', value: product.textureUrl};
  }
  return {kind: 'color', value: product?.color || '#0ea5e9'};
}
